import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
// Components
import Header from '../../Components/Header';
import FormData from '../../Components/FormData';
import Container from '../../Components/Container';
import {
	CheckboxGrid,
	FormSection,
	PrimaryButton,
	SegmentedControl,
	Spinner,
	TextField,
} from '../../Components/ui';
// Services
import { createInstitution } from '../../Services/createInstitution';
// Constants
import { resourcesCategories } from './constants';
import AddCustomCategory from './AddCustomCategory';

const institutionTypeOptions = [
	{ value: 'medicine', label: 'Medicine' },
	{ value: 'technology', label: 'Technology' },
];

const DOMAIN_REGEX = /^https:\/\/.+\/$/;

const notifyError = (message) => toast.error(message, { autoClose: 3000 });

const AdminPage = () => {
	const navigate = useNavigate();

	useEffect(() => {
		if (!localStorage.name || !localStorage.email) {
			navigate('/');
		}
	}, [navigate]);

	const [institutionType, setInstitutionType] = useState(null);
	const [categories, setCategories] = useState([]);
	const [customCategories, setCustomCategories] = useState([]);
	const [name, setName] = useState('');
	const [code, setCode] = useState('');
	const [domain, setDomain] = useState('');
	const [logo, setLogo] = useState(null);
	const [loginMode, setLoginMode] = useState(null);
	const [uniqueUser, setUniqueUser] = useState('');
	const [uniqueUserPass, setUniqueUserPass] = useState('');
	const [selectedResourcesCategories, setSelectedResourcesCategories] =
		useState([]);
	const [loading, setLoading] = useState(false);
	const [messageCreation, setMessageCreation] = useState('');

	// Selección de categorias de institución
	const handleCategoriesChange = (next) => {
		setCategories(next);
	};

	const handleAddCustomCategory = (category) => {
		setCustomCategories((prev) => [...prev, category]);
		setCategories((prev) => [...prev, `custom:${category.id}`]);
	};

	const handleResourcesCategoriesChange = (next) => {
		setSelectedResourcesCategories(next);
	};

	const handleSubmit = (e) => {
		e.preventDefault();

		createInstitution(
			name,
			institutionType,
			code,
			domain,
			logo,
			categories,
			notifyError,
			loginMode === 'requestLogin',
			loginMode === 'uniqueLogin',
			uniqueUser,
			uniqueUserPass,
			setLoading,
			setMessageCreation,
			selectedResourcesCategories,
			customCategories,
		);
	};

	const customCategoryOptions = customCategories.map((category) => ({
		value: `custom:${category.id}`,
		label: category.name,
		image: category.url,
	}));

	const institutionCategoryOptions = customCategoryOptions;

	const resourcesCategoryOptions = resourcesCategories.map((category) => ({
		value: category,
		label: category,
	}));

	return (
		<>
			<Header />
			<h1 className='pt-8 pb-8 text-center text-2xl font-bold text-zinc-900'>
				Admin Panel
			</h1>
			<Container>
				<main className='pb-16'>
					<form onSubmit={handleSubmit} className='flex flex-col gap-8'>
						<FormSection title='Institution name' required>
							<TextField
								id='institution-name'
								value={name}
								onChange={(e) => setName(e.target.value)}
								placeholder='University of Florida'
								required
							/>
						</FormSection>

						<FormSection title='Institution code' required>
							<TextField
								id='institution-code'
								value={code}
								onChange={(e) => setCode(e.target.value)}
								placeholder='UOF'
								required
							/>
						</FormSection>

						<FormSection title='Dominio (url) de la institución' required>
							<TextField
								id='institution-domain'
								value={domain}
								onChange={(e) => setDomain(e.target.value)}
								placeholder='https://cti-library.aquinasnetwork.tech/'
								required
								error={
									domain && !DOMAIN_REGEX.test(domain)
										? 'El dominio debe comenzar con https:// y terminar con /'
										: undefined
								}
							/>
						</FormSection>

						<FormSection title='Tipo de institución' required>
							<SegmentedControl
								options={institutionTypeOptions}
								value={institutionType}
								onChange={setInstitutionType}
								columns={2}
							/>
						</FormSection>

						<FormSection title='Login para la institución'>
							<SegmentedControl
								options={[
									{
										value: 'requestLogin',
										label: 'Requiere login (Canvas, Moodle, Talent, etc..)',
									},
									{
										value: 'uniqueLogin',
										label: 'Requiere login unico',
									},
								]}
								value={loginMode}
								onChange={setLoginMode}
								columns={2}
							/>

							{loginMode === 'uniqueLogin' && (
								<div className='mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2'>
									<TextField
										id='unique-user'
										label='User'
										value={uniqueUser}
										onChange={(e) => setUniqueUser(e.target.value)}
										placeholder='Usuario'
									/>
									<TextField
										id='unique-user-pass'
										label='Pass'
										value={uniqueUserPass}
										onChange={(e) => setUniqueUserPass(e.target.value)}
										placeholder='Contraseña'
									/>
								</div>
							)}
						</FormSection>

						<FormSection title='Logo de la institución (.png)' required>
							<FormData setLogo={setLogo} logo={logo} />
						</FormSection>

						<FormSection
							title='Categorias de institución'
							description='Selecciona las categorías de la institución.'
						>
							<AddCustomCategory onAdd={handleAddCustomCategory} />
							<CheckboxGrid
								options={institutionCategoryOptions}
								value={categories}
								onChange={handleCategoriesChange}
								columns={5}
								withImage
							/>
						</FormSection>

						<FormSection
							title='Categorias de los recursos'
							description='Selecciona las categorías de recursos de la institución.'
						>
							<CheckboxGrid
								options={resourcesCategoryOptions}
								value={selectedResourcesCategories}
								onChange={handleResourcesCategoriesChange}
								columns={4}
							/>
						</FormSection>

						<div className='flex flex-col items-center gap-4'>
							{loading ? (
								<Spinner label='Creando institución...' size='md' />
							) : (
								<PrimaryButton type='submit' size='lg'>
									Crear institución
								</PrimaryButton>
							)}

							{messageCreation && (
								<p className='text-center text-xs font-medium text-zinc-700'>
									{messageCreation}
								</p>
							)}
						</div>
					</form>
				</main>
			</Container>
		</>
	);
};

export default AdminPage;
